/** Conversation with the AI tutor, shared by the «Разговор» tab and the reader panel. */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { askTutor } from '../data/gemini.ts';
import { readJson, readString, writeJson, writeString } from '../data/storage.ts';
import {
  TutorError,
  TUTOR_ERROR_TEXT,
  chatHistoryKey,
  migrateLegacyChat,
  type ChatMessage,
  type TutorContext,
} from '../core/tutor.ts';
const STORED_MESSAGES = 40;

interface TutorValue {
  messages: ChatMessage[];
  pending: boolean;
  error: string | null;
  context: TutorContext;
  /** Text to prefill in the composer (e.g. from «Спросить учителя» in the reader). */
  draft: string;
  setDraft: (text: string) => void;
  setLessonContext: (ctx: LessonContext | null) => void;
  /** The sentence currently open in the reader, if any. */
  setSentence: (sentence: TutorContext['sentence'] | null) => void;
  send: (text: string) => Promise<void>;
  clear: () => void;
}

type LessonContext = Pick<TutorContext, 'lessonTitle' | 'grammar' | 'words'>;

const TutorContextValue = createContext<TutorValue | null>(null);

/**
 * One conversation per language (`lang`): the app mounts the provider keyed by the active
 * course's language, so switching course switches to that language's own chat.
 */
export function TutorProvider({
  lang,
  languageRu,
  children,
}: {
  lang: string;
  languageRu: string;
  children: ReactNode;
}) {
  const historyKey = chatHistoryKey(lang);
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    migrateLegacyChat(readString, writeString);
    return readJson<ChatMessage[]>(historyKey) ?? [];
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [lessonContext, setLessonContext] = useState<LessonContext | null>(null);
  const [sentence, setSentence] = useState<TutorContext['sentence'] | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    writeJson(historyKey, messages.slice(-STORED_MESSAGES));
  }, [historyKey, messages]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const context = useMemo<TutorContext>(
    () => ({ languageRu, ...lessonContext, ...(sentence ? { sentence } : {}) }),
    [languageRu, lessonContext, sentence],
  );

  const send = useCallback(
    async (text: string) => {
      const question = text.trim();
      if (!question || pending) return;
      const history: ChatMessage[] = [...messages, { role: 'user', text: question }];
      setMessages(history);
      setPending(true);
      setError(null);
      abortRef.current = new AbortController();
      try {
        const answer = await askTutor(context, history, abortRef.current.signal);
        setMessages((current) => [...current, { role: 'tutor', text: answer }]);
      } catch (e) {
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setError(e instanceof TutorError ? e.message : TUTOR_ERROR_TEXT.unknown);
      } finally {
        setPending(false);
      }
    },
    [context, messages, pending],
  );

  const clear = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const value = useMemo<TutorValue>(
    () => ({ messages, pending, error, context, draft, setDraft, setLessonContext, setSentence, send, clear }),
    [messages, pending, error, context, draft, send, clear],
  );
  return <TutorContextValue.Provider value={value}>{children}</TutorContextValue.Provider>;
}

export function useTutor(): TutorValue {
  const value = useContext(TutorContextValue);
  if (!value) throw new Error('useTutor must be used inside TutorProvider');
  return value;
}
